import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { groupHeadParts, type ServerGroup } from '../lib/fleet-machines';
import { canNameServer } from '../lib/fleet-rename';
import type { UtilBox } from '../lib/fleetUtilization';
import { createServerNameWrite } from '../lib/name-write';
import { ServerNameForm } from './ServerNameForm';
import { Button } from './ui/Button';

// One Machines group's header (#6560, prototype `groupHead`): the server's name (or
// "Unnamed server, <address>"), how many machines it carries and how they are doing.
//
// "NAME THIS SERVER" (#6566, owner ruling G7, not in the prototype) sits on an UNNAMED
// server's header only; a named server is renamed from the details panel's About. It is an
// admin's (G21) and only on a group holding one of this org's machines (`canNameServer`):
// the write names every own-org machine the UNFILTERED read places on the server, so a
// filter, a search or a page never names a subset, and another org's machines (G23) stay
// unnamed under the same address.

/** A DOM id from a server key (`address:10.9.0.1`, `label:hv 1`): `:`, `.` and spaces are not id-safe in a selector. */
const formId = (key: string): string => `server-name-${key.replace(/[^A-Za-z0-9_-]/g, '-')}`;

const SUMMARY_TONE: Readonly<Record<ServerGroup['summary']['kind'], string>> = {
  overloaded: 'crit',
  attention: 'warn',
  busy: 'neutral',
  healthy: 'neutral',
};

export function MachineGroupHead(props: {
  readonly group: ServerGroup;
  readonly canWrite: boolean;
  /** The UNFILTERED roster: the label write names every own-org machine on the server. */
  readonly allBoxes: readonly UtilBox[];
  readonly token: string | null;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  readonly onWritten: () => void;
}): JSX.Element {
  const head = () => groupHeadParts(props.group, t);
  const [naming, setNaming] = createSignal(false);
  const write = createServerNameWrite({
    all: () => props.allBoxes,
    groupKey: () => props.group.key,
    token: props.token,
    notify: (m, tone) => props.notify(m, tone),
    onWritten: () => props.onWritten(),
    onDone: () => setNaming(false),
  });
  const offered = (): boolean =>
    props.group.name.kind === 'address' &&
    canNameServer(props.allBoxes, props.group.key, props.canWrite);

  return (
    <>
      <h3 class="machine-group__head">
        <span class="machine-group__name">{head().name}</span>
        <span>{head().count}</span>
        <span data-tone={SUMMARY_TONE[props.group.summary.kind]}>{head().summary}</span>
        <Show when={offered()}>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={naming()}
            onClick={() => {
              write.reset();
              setNaming((now) => !now);
            }}
          >
            {t('fleet.machines.server_name')}
          </Button>
        </Show>
      </h3>
      <Show when={naming() && offered()}>
        <ServerNameForm
          id={formId(props.group.key)}
          currentLabel={null}
          pending={write.pending()}
          error={write.error()}
          onSave={write.save}
          onCancel={() => setNaming(false)}
        />
      </Show>
    </>
  );
}
